// Feature flags for the signed-in agent's account. The server evaluates them
// (rollout, per-account overrides); this only mirrors the answer.
//
// Until the first answer arrives — or if it never does — every flag sits at
// its catalog default, so a flag that's on by default doesn't blink off on
// boot. Gates that must not render a default-off feature even briefly wait on
// `loaded`.

import { create } from 'zustand';
import { defaultFeatureFlagValues, type FeatureFlagKey, type FeatureFlagValues } from '@prakash-software/shared';
import { getFeatureFlags } from '../lib/feature-flags-api';

interface FeatureFlagsState {
  flags: FeatureFlagValues;
  /** True once a load finished, successfully or not. */
  loaded: boolean;
}

export const useFeatureFlagsStore = create<FeatureFlagsState>(() => ({
  flags: defaultFeatureFlagValues(),
  loaded: false,
}));

// Bumped on reset, so a load started for the previous agent can't land after
// they sign out and someone else signs in.
let generation = 0;

export async function refreshFeatureFlags(): Promise<void> {
  const gen = generation;
  try {
    const flags = await getFeatureFlags();
    if (gen !== generation) return;
    useFeatureFlagsStore.setState({ flags: { ...defaultFeatureFlagValues(), ...flags }, loaded: true });
  } catch {
    // Keep what we have; the next reconnect retries.
    if (gen === generation) useFeatureFlagsStore.setState({ loaded: true });
  }
}

/** Back to catalog defaults. Called on sign-out. */
export function resetFeatureFlags(): void {
  generation += 1;
  useFeatureFlagsStore.setState({ flags: defaultFeatureFlagValues(), loaded: false });
}

export function useFeatureFlag(key: FeatureFlagKey): boolean {
  return useFeatureFlagsStore((s) => s.flags[key]);
}
